import get from "lodash/get";
import {
  FIELD_CUSTOM_FIELDS_KEY,
  FIELD_METADATA_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useFormValidation } from "PFApp/hooks/activity_form/use_form_validation";
import { Sections } from "PFApp/types/activity_form/activity_form_schema";
import { ParsedSchemaProperty } from "PFCore/types";
import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useActivityFormStore } from "../../activity_form_store";
import { isFieldBlank } from "../../helpers";
import { useSubtemplate } from "../../hooks/use_subtemplate";
import { FieldFactory } from "../components/field_factory/field_factory";
import { RestoreEngagementLinkButton } from "../components/restore_engagement_link_button";
import { FormSection, SectionProps } from "../form_section";

export const AttributesFormSection = ({ id, title }: SectionProps) => {
  const {
    control,
    formState: { errors },
    resetField
  } = useFormContext();
  const { t } = useTranslation("activities");
  const template = useActivityFormStore((state) => state.template);
  const formSchema = useActivityFormStore((state) => state.formSchema);
  const { draft: submittedAsDraft } = useActivityFormStore((state) => state.submitOptions);
  const subtemplate = useSubtemplate();
  const { isFieldRequired } = useFormValidation({ subtemplate, template });

  const properties = useMemo(() => formSchema![Sections.Attributes]!, [formSchema]);

  return (
    <FormSection
      id={id}
      title={title}
      actions={<RestoreEngagementLinkButton />}
      qaId="ActivityForm.attributes"
    >
      {properties.map((field) => {
        const property = field.property as ParsedSchemaProperty;
        const isCustomField = property.type === "custom_field";
        const rootFieldKey = isCustomField ? FIELD_CUSTOM_FIELDS_KEY : FIELD_METADATA_KEY;
        const required = isFieldRequired(field);
        return (
          <Controller
            key={property.name}
            name={`${rootFieldKey}.${property.name}`}
            control={control}
            rules={{
              validate: (value: string) => {
                if (!submittedAsDraft && required && isFieldBlank(value)) {
                  return t("form.errors.cantBeBlank");
                }
                return true;
              }
            }}
            render={({ field, formState: { defaultValues } }) => (
              <FieldFactory
                property={property}
                value={field.value ?? (isCustomField ? [] : undefined)}
                error={
                  (get(errors, field.name)?.message as string) ||
                  (get(errors, property.name)?.message as string)
                }
                onChange={(value) => {
                  if (isFieldBlank(value) && !get(defaultValues, field.name)) {
                    // when clearing attribute which was not initialy set (not in defaultValues),
                    // we want to clear the value together with isDirty flag
                    resetField(field.name, { keepDirty: false, defaultValue: null });
                  } else {
                    field.onChange(value);
                  }
                }}
                required={required}
              />
            )}
          />
        );
      })}
    </FormSection>
  );
};
